import { useState, useEffect } from 'react';
import { Check, X, Edit2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ROOMS } from '@/lib/constants';

interface DetectedItem {
  name: string;
  quantity: number;
  room: string;
  notes?: string;
}

interface AIItemsReviewProps {
  open: boolean;
  items: DetectedItem[];
  onClose: () => void;
  onConfirm: (items: DetectedItem[]) => void;
}

export const AIItemsReview = ({ open, items: initialItems, onClose, onConfirm }: AIItemsReviewProps) => {
  const [editedItems, setEditedItems] = useState(initialItems);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set(initialItems.map((_, i) => i)));
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setEditedItems(initialItems);
      setSelectedItems(new Set(initialItems.map((_, i) => i)));
      setEditingIndex(null);
    }
  }, [open, initialItems]);

  const toggleItem = (index: number) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const updateItem = (index: number, field: keyof DetectedItem, value: string | number) => {
    setEditedItems(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const deleteItem = (index: number) => {
    setEditedItems(prev => prev.filter((_, i) => i !== index));
    setSelectedItems(prev => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  };

  const handleConfirm = () => {
    const selectedItemsList = editedItems.filter((_, i) => selectedItems.has(i));
    onConfirm(selectedItemsList);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review AI-Detected Items</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {editedItems.length} item{editedItems.length !== 1 ? 's' : ''} detected. Review and edit before adding to your box.
          </p>
        </DialogHeader>

        <div className="space-y-2">
          {editedItems.map((item, index) => (
            <div key={index} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
              <Checkbox
                checked={selectedItems.has(index)}
                onCheckedChange={() => toggleItem(index)}
                className="mt-1"
              />
              
              {editingIndex === index ? (
                <div className="flex-1 space-y-2">
                  <Input
                    value={item.name}
                    onChange={(e) => updateItem(index, 'name', e.target.value)}
                    placeholder="Item name"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                      placeholder="Quantity"
                    />
                    <Select
                      value={item.room}
                      onValueChange={(value) => updateItem(index, 'room', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROOMS.map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input
                    value={item.notes || ''}
                    onChange={(e) => updateItem(index, 'notes', e.target.value)}
                    placeholder="Notes (optional)"
                  />
                  <Button size="sm" onClick={() => setEditingIndex(null)}>
                    <Check className="h-4 w-4 mr-1" />
                    Done
                  </Button>
                </div>
              ) : (
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium">{item.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        Qty: {item.quantity} • {item.room}
                      </p>
                      {item.notes && (
                        <p className="text-sm text-muted-foreground mt-1">{item.notes}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingIndex(index)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteItem(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={selectedItems.size === 0}>
            Add {selectedItems.size} Item{selectedItems.size !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
